<template>
	<div>
		<h1>Planification</h1>

		<p>
			Type
			<b-select v-model="type_choice" :options="[
				{ text: 'Projet', value: 'projet' },
				{ text: 'Base de données', value: 'db' },
				{ text: 'Mail', value: 'mail' },
				{ text: 'Vérification fine tuning - ChatGPT', value: 'fine_tuning' }
			]" />
		</p>

		<p v-if="type_choice !== 'fine_tuning'">
			Projet
			<v-select v-model="project" :options="list_projects" />
		</p>

		<p v-if="type_choice !== 'fine_tuning'">
			Intervalle
			<div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
				<b-input v-model="repeatInterval" placeholder="ex: 0 8 * * *" style="max-width: 320px;" @input="on_repeat_interval_manual_edit" />
				<b-button size="sm" variant="outline-secondary" @click="toggle_cron_builder">
					<i class="fas fa-magic"></i> Générateur cron
				</b-button>
			</div>
			<b-card v-if="show_cron_builder" bg-variant="light" class="mt-2 mb-2">
				<CronLight :key="cron_builder_key" v-model="cron_builder_value" locale="fr" :periods="cron_periods_builder" :custom-locale="cron_locale_fr" />
			</b-card>
		</p>
		<p>
			Date
			<b-input type="datetime-local" v-model="nextRunAt" />
		</p>

		<!-- ===== PROJET ===== -->
		<template v-if="type_choice === 'projet'">
			<p>
				<b-checkbox v-model="only_one">Ne pas déclencher si il y a déjà un traitement</b-checkbox>
			</p>

			<div class="mt-2">
				<strong>Tableau</strong>
				<small class="text-muted d-block mb-1">Données injectées comme fichier initial du scénario (une ligne par objet) — saisie manuelle ou import d'un csv/xlsx</small>
				<edit_tabulator ref="json_tabulator" :extern_file="true" />
			</div>

			<div class="mt-2">
				<strong>Fichiers</strong>
				<b-table-simple small bordered class="mt-1">
					<b-thead>
						<b-tr>
							<b-th>Nom</b-th>
							<b-th>Url</b-th>
							<b-th></b-th>
						</b-tr>
					</b-thead>
					<b-tbody>
						<b-tr v-for="(row, idx) in files_data" :key="'agf' + idx">
							<b-td>
								<b-input v-model="row.name" size="sm" />
							</b-td>
							<b-td>
								<b-input v-model="row.url" size="sm" />
							</b-td>
							<b-td>
								<b-button size="sm" variant="danger" @click="files_data.splice(idx, 1)">✕</b-button>
							</b-td>
						</b-tr>
					</b-tbody>
				</b-table-simple>
				<b-button size="sm" @click="files_data.push({ name: '', type: 'import', url: '', format: '' })">+ Ajouter un fichier</b-button>
			</div>
		</template>

		<!-- ===== BASE DE DONNÉES ===== -->
		<template v-if="type_choice === 'db'">
			<p>
				Table
				<b-input v-model="table" />
			</p>
			<p>
				Conditions
				<b-input v-model="where" />
			</p>
			<p>
				Colonne de prise en compte
				<b-input v-model="column_process" />
			</p>
		</template>

		<!-- ===== MAIL ===== -->
		<template v-if="type_choice === 'mail'">
			<p>
				Récupérer pièces avec les extensions suivantes
				<b-input v-model="extension" placeholder="ex: pdf,csv" />
			</p>
			<p>
				Email
				<b-input v-model="user" />
			</p>
			<p>
				Mot de passe
				<b-input type="password" v-model="password" />
			</p>
			<p>
				Host
				<b-input v-model="host" />
			</p>
			<p>
				Port
				<b-input type="number" v-model.number="port" />
			</p>
		</template>

		<!-- ===== FINE TUNING ===== -->
		<template v-if="type_choice === 'fine_tuning'">
			<p>
				Id de chatgpt du fine tuning
				<b-input v-model="id_fine_tuning" />
			</p>
			<p>
				Utilisateur
				<b-input v-model="client_user_uid" />
			</p>
		</template>

		<br />

		<b-button @click="update">Mise à jour</b-button>&nbsp;
		<b-button @click="execute">Executer maintenant</b-button>&nbsp;
		<b-button @click="delete_job">Supprimer</b-button>
	</div>
</template>

<script>
	import edit_tabulator from "../components/edit_tabulator.vue";

	import store from "../store";
	import agenda from "../services/agenda.js";
	import projects from "../services/projects.js";
	import { cron_locale_fr, cron_periods_fr } from "../utils/cron_locale_fr.js";
	import { cron_names_to_numbers } from "../utils/cron_names_to_numbers.js";
	import { order_periods_for_value } from "../utils/cron_period_order.js";

	export default {
		name: "agenda_job",
		components: { edit_tabulator },
		data() {
			return {
				list_projects: [],
				list_projects_libelle: {},
				list_projects_uid: {},
				job: { data: {} },

				type_choice: "projet",
				project: "",
				repeatInterval: "",
				nextRunAt: "",
				show_cron_builder: false,
				cron_periods_builder: cron_periods_fr,
				cron_builder_key: 0,

				// Projet
				only_one: false,
				json: [],
				files_data: [],

				// Base de données
				table: "",
				where: "",
				column_process: "",

				// Mail
				extension: "",
				user: "",
				password: "",
				host: "",
				port: 993,

				// Fine tuning
				id_fine_tuning: "",
				client_user_uid: "",

				loading_form: false,
			};
		},
		computed: {
			cron_periods_fr() {
				return cron_periods_fr;
			},
			cron_locale_fr() {
				return cron_locale_fr;
			},
			cron_builder_value: {
				get() {
					return cron_names_to_numbers(this.repeatInterval);
				},
				set(value) {
					this.repeatInterval = value;
				}
			}
		},
		watch: {
			type_choice() {
				if (this.loading_form) return;
				this.repeatInterval = "";
				this.nextRunAt = new Date().toISOString().slice(0, -8);
			},
		},
		methods: {
			toggle_cron_builder() {
				if (!this.show_cron_builder) {
					this.cron_periods_builder = order_periods_for_value(cron_periods_fr, this.repeatInterval);
					this.cron_builder_key++;
				}
				this.show_cron_builder = !this.show_cron_builder;
			},
			on_repeat_interval_manual_edit(value) {
				if (!this.show_cron_builder) return;
				// L'édition directe du champ texte peut renseigner des champs
				// (ex: jour du mois) hors de la période actuellement affichée par
				// le générateur visuel : celui-ci les remettrait sinon à "*" au
				// prochain changement interne. On recalcule la période la plus
				// adaptée et on force un remontage complet du widget (via :key)
				// pour qu'il reparte bien de la valeur saisie.
				this.cron_periods_builder = order_periods_for_value(cron_periods_fr, value);
				this.cron_builder_key++;
			},
			async execute() {
				this.syncJobFromForm();
				await agenda.now(this.$route.params.id, this.job);
			},
			async delete_job() {
				var result = await agenda.delete(this.$route.params.id);
				this.$router.push({ path: "/planification" });
			},
			async update() {
				this.syncJobFromForm();

				var result;
				try {
					result = await agenda.update(
						this.$route.params.id,
						this.job
					);
				} catch (error) {
					this.$notify({
						group: "info",
						title: "Une erreur c'est produite durant l'enregistrement",
						text: (error.response && error.response.data && error.response.data.msg) || "",
						type: "error",
					});
					return;
				}

				this.job = result.job;

				this.$notify({
					group: "info",
					title: "Planification enregistrée",
					type: "success",
				});

				if (this.job._id)
					this.$router.push({
						path: "/planification/" + this.job._id,
					});
				else this.$router.push({ path: "/planification" });

				await this.loadFormFromJob(this.job);
			},
			syncJobFromForm() {
				if (!this.job.data) this.job.data = {};

				this.job.repeatInterval = this.repeatInterval;
				this.job.nextRunAt = this.nextRunAt;

				if (this.type_choice == "mail") {
					this.job.data.type = "mail";
					this.job.data.project_uid = this.list_projects_libelle[this.project];
					this.job.data.extension = this.extension;
					this.job.data.user = this.user;
					this.job.data.host = this.host;
					this.job.data.port = this.port;
					this.job.data.password = this.password;
				}
				else if (this.type_choice == "db") {
					this.job.data.type = "db";
					this.job.data.project_uid = this.list_projects_libelle[this.project];
					this.job.data.table = this.table;
					this.job.data.where = this.where;
					this.job.data.column_process = this.column_process;
				}
				else if (this.type_choice == "fine_tuning") {
					this.job.data.type = "fine_tuning";
					this.job.data.id_fine_tuning = this.id_fine_tuning;
					this.job.data.client_user_uid = this.client_user_uid;
				}
				else {
					this.job.data.type = "create";
					this.job.data.project_uid = this.list_projects_libelle[this.project];
					this.job.data.files = this.files_data;
					this.job.data.json = this.$refs.json_tabulator
						? this.$refs.json_tabulator.get_data()
						: this.json;
					this.job.data.only_one = this.only_one;
				}
			},
			async loadFormFromJob(job) {
				this.loading_form = true;

				if (!job) job = {};
				var data = job.data || {};

				this.type_choice =
					data.type == "mail" ? "mail" :
					data.type == "db" ? "db" :
					data.type == "fine_tuning" ? "fine_tuning" :
					"projet";

				this.project = this.list_projects_uid[data.project_uid] || "";
				this.repeatInterval = job.repeatInterval || "";
				this.nextRunAt = job.nextRunAt
					? job.nextRunAt.replace("Z", "").replace(/\.\d{3}/, "")
					: new Date().toISOString().slice(0, -8);

				this.only_one = !!data.only_one;
				this.json = Array.isArray(data.json) ? data.json : [];
				this.files_data = Array.isArray(data.files) ? data.files : [];

				this.table = data.table || "";
				this.where = data.where || "";
				this.column_process = data.column_process || "";

				this.extension = data.extension || "";
				this.user = data.user || "";
				this.password = data.password || "";
				this.host = data.host || "";
				this.port = data.port !== undefined ? data.port : 993;

				this.id_fine_tuning = data.id_fine_tuning || "";
				this.client_user_uid = data.client_user_uid || "";

				await this.$nextTick();
				if (this.$refs.json_tabulator)
					await this.$refs.json_tabulator.set_data(this.json);

				this.loading_form = false;
			},
			getProjects: async function () {
				try {
					var list_projects = await projects.getAll(true, false, false, false, true);
					for (var i = 0; i < list_projects.length; i++) {
						if (list_projects[i].libelle) {
							this.list_projects_libelle[
								list_projects[i].libelle
							] = list_projects[i].uid;
							this.list_projects_uid[list_projects[i].uid] =
								list_projects[i].libelle;
							this.list_projects.push(list_projects[i].libelle);
						}
					}
				} catch (error) {
					console.log(error);
				}
			},
		},
		created: async function () {
			if (
				store.state.user.rules &&
				!store.state.user.rules.planification
			) {
				this.$router.push({ name: "profile" });
			}

			await this.getProjects();

			this.job = await agenda.get(this.$route.params.id);
			this.job = this.job.jobs[0];

			if (!this.job) {
				this.job = {
					data: {
						project_uid:
							this.list_projects_libelle[this.list_projects[0]],
					},
					repeatInterval: "",
					nextRunAt: new Date().toISOString().slice(0, -8),
				};
			}

			await this.loadFormFromJob(this.job);
		},
	};
</script>

<style></style>
